import { Grid } from '@material-ui/core';
import {
  Button, IconButton, Panel, useDialogs,
} from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { getOptionLabel } from '@michelin/select-options-provider';
import { Delete as DeleteIcon, Edit } from '@material-ui/icons';
import React, { useState } from 'react';
import DataGrid, {
  SearchPanel,
  FilterRow,
  Column,
  Scrolling,
} from 'devextreme-react/data-grid';
import { v4 as uuidv4 } from 'uuid';
import { Contact, NotificationSubscription } from '../../../Types';
import { PlainMessage } from '../../PlainMessage';
import { NotificationSubscriptionsDetail } from './Detail';

interface NotificationSubscriptionsListProps{
  contact: Contact;
  action: string;
  onChange?: (notifications: NotificationSubscription[]) => void;
  readOnly: boolean;
}

const NotificationSubscriptionsList = (props:NotificationSubscriptionsListProps) => {
  const { t } = useTranslation();
  const { confirmDialog, messageDialog, errorDialog } = useDialogs();

  const [listData, setListData] = useState(props.contact.notification_subscription || []);
  const [detailAction, setDetailAction] = useState('none');
  const [detailData, setDetailData] = useState<any>();

  /// //////////////////////////////////////////////////////////////////
  /// CHECK METHODS
  /// //////////////////////////////////////////////////////////////////
  const isDuplicate = (data: any, list:any): boolean => list.some((item:NotificationSubscription) => item.event === data.event && item.type === data.type && item.id !== data.id);

  /// //////////////////////////////////////////////////////////////////
  /// GET INFO METHODS
  /// //////////////////////////////////////////////////////////////////
  const getNotificationTypeLabel = (notification: NotificationSubscription): string => {
    let str: string = '';
    if (notification.event === 'Profile') {
      str = getOptionLabel('dealer_contact_notifications_profile_types', notification.type);
    } else if (notification.event === 'commercial_fleet_ordering') {
      str = getOptionLabel('dealer_contact_notifications_commercial_fleet_ordering_types', notification.type);
    } else if (notification.event === 'urban_fleet_ordering') {
      str = getOptionLabel('dealer_contact_notifications_urban_fleet_ordering_types', notification.type);
    } else if (notification.event === 'tire_ers') {
      str = getOptionLabel('dealer_contact_notifications_tire_ers_types', notification.type);
    }
    return str;
  };
  const getNotificationMethodLabel = (notification: NotificationSubscription): string => {
    let str: string = '';
    let separator = '';
    notification.method.forEach((e: string) => {
      let strAux: string = '';
      strAux += separator + getOptionLabel('dealer_contact_notifications_methods', e);
      str += strAux.length ? strAux : e;
      separator = ', ';
    });
    return str;
  };
  const getCCText = (notification: NotificationSubscription): string => {
    if (!notification.cc_emails) return '';
    if (notification.cc_emails.length === 0) return '';
    if (notification.cc_emails.length === 1) { return `${notification.cc_emails[0]}`; }
    return t('{{emailsCount}} Emails', {
      emailsCount: notification.cc_emails.length,
    });
  };
  const getCCEmails = (notification: NotificationSubscription): string[] => {
    if (!notification.cc_emails) return [''];
    const resp: string[] = [];
    notification.cc_emails.forEach((email) => {
      resp.push(`${email}`);
    });
    return resp;
  };

  /// //////////////////////////////////////////////////////////////////
  /// UPDATE METHODS
  /// //////////////////////////////////////////////////////////////////

  const deleteNotification = (notification: NotificationSubscription) => {
    const auxList = listData.filter((elem:NotificationSubscription) => elem.id !== notification.id);
    setListData(auxList);
    if (props.onChange) props.onChange(auxList);
  };

  const saveNotification = (notification: NotificationSubscription) => {
    const auxNotification = { ...notification };
    let auxList: NotificationSubscription[] = [];
    if (listData) auxList = [...listData];
    let duplicateNotification:boolean = false;
    // The ID is used to know if the data is edited or added
    if (notification.id) {
      duplicateNotification = isDuplicate(auxNotification, auxList);
      if (!duplicateNotification) {
        setDetailAction('none');
        setDetailData(undefined);
        auxList = auxList.filter((item) => item.id !== auxNotification.id);
        auxList.push(auxNotification);
        setListData(auxList);
        if (props.onChange) props.onChange(auxList);
      }
    } else {
      duplicateNotification = isDuplicate(auxNotification, auxList);
      if (!duplicateNotification) {
        setDetailAction('none');
        setDetailData(undefined);
        auxNotification.id = uuidv4();
        auxList.push(auxNotification);
        setListData(auxList);
        if (props.onChange) props.onChange(auxList);
      }
    }

    if (duplicateNotification) {
      errorDialog(
        t(
          "You've attempted to add a duplicate notification with the following information\nEvent: {{notificationEvent}}\nType: {{notificationType}}",
          {
            notificationEvent: getOptionLabel('dealer_contact_notifications_events', notification.event),
            notificationType: getNotificationTypeLabel(notification),
          },
        ).split('\n'),
        t('Duplicated notification subscription found'),
      );
    }
  };

  /// //////////////////////////////////////////////////////////////////
  /// COLUMN RENDER METHODS
  /// //////////////////////////////////////////////////////////////////

  function renderEvent(notification: NotificationSubscription): JSX.Element {
    return (
      <div>
        <b>{getOptionLabel('dealer_contact_notifications_events', notification.event)}</b>
      </div>
    );
  }

  function renderType(notification: NotificationSubscription): JSX.Element {
    return (
      <div>
        <b>{getNotificationTypeLabel(notification)}</b>
      </div>
    );
  }

  function renderMethod(notification: NotificationSubscription): JSX.Element {
    return (
      <div>
        <b>{getNotificationMethodLabel(notification)}</b>
      </div>
    );
  }

  function renderCC(notification: NotificationSubscription): JSX.Element {
    const dialogTitle: string = `${getOptionLabel('dealer_contact_notifications_events', notification.event)} / ${getNotificationTypeLabel(notification)}`;
    const dialogMessages = getCCEmails(notification);
    return (
      <div
        style={{ cursor: 'pointer' }}
        onClick={() => {
          messageDialog(dialogMessages, dialogTitle);
        }}
      >
        <b>{getCCText(notification)}</b>
      </div>
    );
  }

  function renderActions(
    notification: NotificationSubscription,
  ): JSX.Element {
    const data : NotificationSubscription = { ...notification };
    return (
      <div>
        <IconButton
          aria-label={t('Edit')}
          style={{ margin: '8px' }}
          onClick={() => {
            setDetailAction('edit');
            setDetailData(data);
          }}
          size="small"
        >
          <Edit fontSize="small" />
        </IconButton>

        <IconButton
          aria-label={t('Delete')}
          style={{ margin: '8px' }}
          onClick={() => confirmDialog(
            t('Do you want to remove this notification subscription?').split(
              '\n',
            ),
            t('Delete subscription'),
            t('Remove subscription'),
            t('Keep subscription'),
            () => {
              deleteNotification(notification);
            },
          )}
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </div>
    );
  }

  /// //////////////////
  // ELEMENTS
  /// //////////////////
  function renderPanelActions() {
    const readOnly: boolean = props.action === 'view' || props.readOnly;
    return (
      <span style={{ position: 'relative', top: '4px' }}>
        {readOnly ? null : (
          <Button
            size="small"
            color="primary"
            onClick={() => { setDetailAction('add'); }}
          >
            {t('Add')}
          </Button>
        )}
      </span>
    );
  }
  function renderNotificationsView() {
    return (
      <DataGrid
        dataSource={listData}
        allowColumnReordering
        allowColumnResizing
        keyExpr="event"
      >
        <FilterRow visible={false} />
        <Scrolling mode="virtual" />
        <SearchPanel visible={false} highlightCaseSensitive={false} />
        <Column
          caption={t('Event')}
          cellRender={({ row }) => renderEvent(row.data)}
          allowSorting
          calculateSortValue={(a: NotificationSubscription) => a.event}
          width={200}
        />
        <Column
          caption={t('Notification Type')}
          cellRender={({ row }) => renderType(row.data)}
          allowSorting
          calculateSortValue={(a: NotificationSubscription) => a.type}
        />
        <Column
          caption={t('Method')}
          cellRender={({ row }) => renderMethod(row.data)}
        />
        <Column
          caption={t('CC Email')}
          cellRender={({ row }) => renderCC(row.data)}
        />
        {props.action !== 'view' && !props.readOnly ? (
          <Column
            cellRender={({ row }) => renderActions(row.data)}
            width={110}
          />
        ) : null}
      </DataGrid>
    );
  }

  // /////////////////////////////////
  // RETURN
  // /////////////////////////////////
  if (!listData) {
    return (
      <PlainMessage
        title={t('Error loading data')}
        messages={[t('Data object is empty...')]}
      />
    );
  }
  return (
    <>
      <Panel
        title={t('Notification Subscriptions')}
        control={renderPanelActions()}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {detailAction !== 'none' ? (
              <NotificationSubscriptionsDetail
                onSave={saveNotification}
                action={detailAction}
                data={detailData}
                onClose={() => {
                  setDetailAction('none');
                  setDetailData(undefined);
                }}
              />
            ) : null}
            {!listData.length ? (
              <PlainMessage
                title={t('Notification Subscriptions List Empty')}
                messages={t(
                  'There are currently no subscriptions for this contact.',
                ).split('\n')}
              />
            ) : (
              renderNotificationsView()
            )}
          </Grid>
        </Grid>
      </Panel>
    </>
  );
};
export { NotificationSubscriptionsList };
